import type { StepCard } from "../types.js";
import { displayRows, MARK, type DisplayRow, type LedgerLine, type MarkedLine, type PartyLine } from "../schema/formModel.js";
import "./styles.css";

/**
 * KYC-design — declaration copy as PARAGRAPHS and LISTS without any markup injection: a blank
 * line separates paragraphs, a line starting "- " is a list item. Nothing else is interpreted,
 * so declaration text can never become HTML (fences.test.ts).
 */
export function renderBlocks(text: string): Array<{ kind: "p"; text: string } | { kind: "ul"; items: string[] }> {
  const blocks: Array<{ kind: "p"; text: string } | { kind: "ul"; items: string[] }> = [];
  for (const chunk of text.split(/\n\s*\n/)) {
    const lines = chunk.split("\n").map((l) => l.trim()).filter((l) => l.length > 0);
    if (lines.length === 0) continue;
    if (lines.every((l) => l.startsWith("- "))) {
      blocks.push({ kind: "ul", items: lines.map((l) => l.slice(2).trim()) });
    } else {
      blocks.push({ kind: "p", text: lines.join(" ") });
    }
  }
  return blocks;
}

export function Copy({ text, className }: { text: string; className?: string }) {
  const blocks = renderBlocks(text);
  return (
    <div className={className}>
      {blocks.map((block, i) =>
        block.kind === "p"
          ? <p key={i}>{block.text}</p>
          : <ul key={i}>{block.items.map((item, j) => <li key={j}>{item}</li>)}</ul>,
      )}
    </div>
  );
}

/**
 * Phase 532 — LABEL / VALUE ROWS, the ONE rendering of a declared `{label, path}` row (a step
 * card's `rows`, a field's `from_ctx.evidence`). Both arrive here already resolved against the
 * served ctx snapshot — this component holds no paths, no schema and no snapshot, so there is no
 * second place a declaration could be read from.
 *
 * <p>A `string[]` value is a LIST and renders as one: an assessment ledger is items, and a joined
 * sentence is not a ledger. Everything else is one line. What a row does about a value the file
 * does not carry is decided by the projection ({@link displayRows} renders an explicit
 * "not recorded"; evidence under a question drops the row instead), never here.</p>
 *
 * <p><b>Phase 559 (card #2116, L-97 rule a) — the VISUAL REFERENCE is the "Risk ledger" panel of
 * the approved design sample, https://claude.ai/artifact/KqtoT3aU4QR1JryqxCfAvW</b>: one line per
 * factor, laid out label · source · signed points, with the sign carrying the colour. That panel
 * is what the approval page of 2026-09-21 was supposed to look like when it instead printed
 * twenty-eight `Key: Value` lines. Two value shapes were added for it — {@link LedgerLine} and
 * {@link PartyLine} — and both are chosen by {@link formModel}'s projector from the row's OWN
 * declaration. This component holds NO vocabulary: it never maps a value to a word, never reads
 * a `words` map and never names a ctx key. It decides layout, and the sign's colour, and nothing
 * else.</p>
 */
export function DisplayRowList({ rows, className }: { rows: ReadonlyArray<DisplayRow>; className: string }) {
  if (rows.length === 0) return null;
  return (
    <dl className={className}>
      {rows.map((row, i) => (
        // `display: contents` keeps the two-column grid on the <dl> while the pair stays one
        // node — a <div> wrapper is the only DOM shape that is both valid inside a <dl> and
        // keyed per row.
        <div key={`${i}-${row.label}`} style={{ display: "contents" }}>
          {/* Walk 2 — a structured value (any list) spans both columns under its caption; a
              scalar sits in the narrow value column beside it. */}
          <dt className={Array.isArray(row.value) ? "display-row-wide" : undefined}>{row.label}</dt>
          <dd className={Array.isArray(row.value) ? "display-row-wide" : undefined}>
            {isLedgerLines(row.value) ? <LedgerLines lines={row.value} />
              : isPartyLines(row.value) ? <PartyLines lines={row.value} />
              : isMarkedLines(row.value) ? <MarkedLines lines={row.value} />
              : Array.isArray(row.value)
              ? <ul className="display-row-list">{row.value.map((item, j) => <li key={`${j}-${item}`}>{item}</li>)}</ul>
              : row.tone !== undefined
              // Walk 2 — a toned scalar reads as a pill in the design system's feedback colour
              // for what it MEANS (good / caution / bad); the class names carry the token, so no
              // colour is spelled here.
              ? <span className={`display-tone display-tone-${row.tone}`}>{row.value}</span>
              : row.value}
            {row.note !== undefined && row.note.length > 0 && <p className="display-row-note">{row.note}</p>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * The two structured arms are told apart by the SHAPE of their elements, not by a flag on the
 * row: the projector already decided which arm this is, and a discriminator field would be a
 * second copy of that decision for the renderer to get wrong. A `string[]` never matches
 * either, so the list branch above is reached exactly as before.
 */
function isLedgerLines(value: DisplayRow["value"]): value is LedgerLine[] {
  return Array.isArray(value) && value.length > 0
    && typeof value[0] === "object" && value[0] !== null && "points" in value[0];
}

function isMarkedLines(value: DisplayRow["value"]): value is MarkedLine[] {
  return Array.isArray(value) && value.length > 0
    && typeof value[0] === "object" && value[0] !== null && "marked" in value[0];
}

/**
 * Walk 2 — a MARKED LIST (the engine's generated SIC-code evidence): one item per code, the ones
 * the server marked as matching this question highlighted with {@link MARK} beside them. The row's
 * `note` — the legend saying what the mark means and citing its source — renders under the list.
 * The colour lives in `styles.css` against the class name; no colour literal here.
 */
function MarkedLines({ lines }: { lines: ReadonlyArray<MarkedLine> }) {
  return (
    <ul className="marked-lines">
      {lines.map((line, i) => (
        <li className={line.marked ? "marked-line marked-line-match" : "marked-line"} key={`${i}-${line.text}`}>
          {line.text}
          {line.marked && <span className="marked-line-mark">{` ${MARK}`}</span>}
        </li>
      ))}
    </ul>
  );
}

function isPartyLines(value: DisplayRow["value"]): value is PartyLine[] {
  return Array.isArray(value) && value.length > 0
    && typeof value[0] === "object" && value[0] !== null && "party" in value[0];
}

/**
 * U+2212 MINUS SIGN — the typographic minus, not the hyphen a keyboard produces. Spelled as an
 * escape so it cannot be silently normalised back to a hyphen by an editor or a merge.
 */
export const LEDGER_MINUS = "−";

/**
 * How a signed contribution READS: `+100` for a factor that added risk, `−100` for one that
 * took it away, `0` for a line the declaration chose to keep (`hide_zero: false`).
 */
export function signedPoints(points: number): string {
  if (points > 0) return `+${points}`;
  if (points < 0) return `${LEDGER_MINUS}${Math.abs(points)}`;
  return "0";
}

/**
 * Which token a point value is coloured with. ADDED risk is the error token, REMOVED risk the
 * success token — the pairing the Risk ledger panel shows. The token itself lives in
 * `styles.css` against these class names; no colour literal is spelled in this file, so a
 * design-token change reaches the page without a component edit.
 */
function pointsClass(points: number): string {
  if (points > 0) return "ledger-points ledger-points-adds";
  if (points < 0) return "ledger-points ledger-points-reduces";
  return "ledger-points ledger-points-nil";
}

/** `shows: ledger` — the Risk ledger panel: one line per factor, in DECLARATION order. */
function LedgerLines({ lines }: { lines: ReadonlyArray<LedgerLine> }) {
  return (
    <ul className="ledger-lines">
      {lines.map((line, i) => (
        <li className="ledger-line" key={`${i}-${line.label}`}>
          <span className="ledger-line-label">{line.label}</span>
          {line.source !== undefined && <span className="ledger-line-source">{line.source}</span>}
          <span className={pointsClass(line.points)}>{signedPoints(line.points)}</span>
        </li>
      ))}
    </ul>
  );
}

/** `shows: per_party` — one line per party, captioned by WHO it is about. */
function PartyLines({ lines }: { lines: ReadonlyArray<PartyLine> }) {
  return (
    <ul className="party-lines">
      {lines.map((line, i) => (
        <li className="party-line" key={`${i}-${line.party}`}>
          <span className="party-line-name">{line.party}</span>
          <span className="party-line-value">{line.value}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The step's cards. Since Phase 532 a card may also declare `rows` — READ-ONLY label/value pairs
 * the reader needs in front of them before they answer (an assessment ledger, a total, a computed
 * band) — resolved against the served ctx snapshot the caller passes in, through the SAME
 * projection and the SAME row rendering a question's evidence uses.
 */
export function InfoCards({
  cards,
  ctx,
}: {
  cards: ReadonlyArray<StepCard>;
  /** The validate response's ctx snapshot. Absent ⇒ a card's `rows` resolve to nothing. */
  ctx?: Readonly<Record<string, unknown>>;
}) {
  if (cards.length === 0) return null;
  return (
    <div className="info-cards">
      {cards.map((card, i) => {
        // A card carrying ONLY rows is a legitimate shape (a ledger needs no prose), so the copy
        // half is optional here even though the grammar's `content` is not.
        const blocks = renderBlocks(card.content ?? "");
        const rows = displayRows(card.rows, ctx ?? {});
        const wide = blocks.some((b) => b.kind === "ul" && b.items.length > 3) || rows.length > 0;
        return (
          <section className={`info-card${wide ? " info-card-wide" : ""}`} key={i}>
            <h3 className="info-card-title">{card.title}</h3>
            {blocks.map((block, j) =>
              block.kind === "p"
                ? <p key={j}>{block.text}</p>
                : <ul key={j}>{block.items.map((item, k) => <li key={k}>{item}</li>)}</ul>,
            )}
            <DisplayRowList rows={rows} className="info-card-rows" />
          </section>
        );
      })}
    </div>
  );
}

export function NextSteps({ steps, title = "What happens next" }: { steps: ReadonlyArray<string>; title?: string }) {
  if (steps.length === 0) return null;
  return (
    <section className="next-steps" aria-label={title}>
      <h3 className="next-steps-title">{title}</h3>
      <ol>{steps.map((s, i) => <li key={i}><span>{s}</span></li>)}</ol>
    </section>
  );
}
